import { useCallback, useEffect, useMemo, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { cancelAgentTask, listTasks, type AgentTask } from "@/services/agentApi";

const SEEN_STORAGE_KEY = "tower-agent-tasks-seen-utc";
const POLL_INTERVAL_MS = 60_000;

export interface AgentTasksState {
  tasks: AgentTask[];
  /** Tasks completed since the drawer was last opened — the launcher badge. */
  unreadCount: number;
  refresh(): Promise<void>;
  cancel(taskId: string): Promise<void>;
  markSeen(): void;
}

function loadSeenStamp(): string {
  try {
    return localStorage.getItem(SEEN_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

/**
 * Scheduled-task state for the drawer (#677): a light poll of the task list.
 * Completion delivery is poll-latency for now — instant push rides the general
 * SignalR channel in phase 2. Poll failures are swallowed (the agent service
 * being down already surfaces through the drawer's availability probe).
 */
export function useAgentTasks(enabled: boolean): AgentTasksState {
  const posthog = usePostHog();
  const [tasks, setTasks] = useState<AgentTask[]>([]);
  const [seenStamp, setSeenStamp] = useState(loadSeenStamp);

  const refresh = useCallback(async () => {
    try {
      const list = await listTasks(1, 20);
      setTasks(list.items);
    } catch {
      // Leave the last known list in place; the next poll retries.
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // Poll in an effect. Failures keep the last list; this is not a query cache.
    // oxlint-disable-next-line react/set-state-in-effect
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled, refresh]);

  const unreadCount = useMemo(
    () =>
      tasks.filter(
        (t) =>
          t.status === "completed" && t.completedAtUtc !== null && t.completedAtUtc > seenStamp,
      ).length,
    [tasks, seenStamp],
  );

  const markSeen = useCallback(() => {
    const stamp = new Date().toISOString();
    try {
      localStorage.setItem(SEEN_STORAGE_KEY, stamp);
    } catch {
      // Per-viewer convenience only; losing it just re-shows the badge.
    }
    setSeenStamp(stamp);
  }, []);

  const cancel = useCallback(
    async (taskId: string) => {
      await cancelAgentTask(taskId);
      posthog.capture("agent_task_cancelled");
      await refresh();
    },
    [refresh, posthog],
  );

  return { tasks, unreadCount, refresh, cancel, markSeen };
}
